/**
 * Single-page approval chain builder form.
 *
 * Renders the approval chain name, the two auto-apply toggles, and a reorderable list
 * of up to `MAX_STEPS` steps (each a `ApprovalChainStepRow`). Owns the
 * `react-hook-form` instance + the dnd-kit context; validation lives in
 * `../../model/schema`. Submission is delegated to the parent via `onSubmit`
 * (which receives the mapped Create/Update DTO), so this component stays free of
 * data-fetching and navigation concerns.
 */
import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
} from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';

import { useSortableList } from '@/shared/hooks';

import {
  createDefaultFormData,
  createEmptyStep,
  mapFormDataToDto,
  MAX_STEPS,
  approvalChainFormSchemaFor,
  type ApprovalChainAssigneeOption,
  type ApprovalChainFormData,
  type ApprovalChainFormInput,
} from '../../model/schema';
import type { StepRoleCatalogue } from '../ApproverPicker';
import { ApprovalChainStepRow } from './ApprovalChainStepRow';
import { useApprovalChainFormContext } from './useApprovalChainFormContext';
import { useShowSavedStepShortfalls } from './useShowSavedStepShortfalls';
import { useTranslation } from 'react-i18next';

export interface ApprovalChainFormProps {
  /** Prefilled values for edit mode; omit for a fresh create flow. */
  initialValues?: ApprovalChainFormInput;
  /** Project members the assignee picker offers before any search. */
  members: ApprovalChainAssigneeOption[];
  /** True while the project roster is still loading. */
  isLoadingMembers: boolean;
  /** Workspace id for per-row workspace-user search. */
  workspaceId: string | null | undefined;
  /** The roles a step may name, read once for every row. */
  stepRoles: StepRoleCatalogue;
  /** True while the create/update mutation is in flight. */
  isSubmitting: boolean;
  /** Label for the primary action ("Create approval chain" / "Save changes"). */
  submitLabel: string;
  /** Receives the mapped DTO once the form validates. */
  onSubmit: (dto: ReturnType<typeof mapFormDataToDto>) => void;
  onCancel: () => void;
  /**
   * Reports edits outward so a host that can be dismissed — the side panel —
   * can warn before discarding them. The form keeps owning its
   * `react-hook-form` instance; only the flag crosses the boundary.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /** Why the server refused the auto-apply toggle on the last save, shown under it. */
  autoApplyConflictMessage?: string | null;
}

export function ApprovalChainForm(props: ApprovalChainFormProps) {
  const { t } = useTranslation('approvalChains');

  const { members, isLoadingMembers, workspaceId, isSubmitting, submitLabel } = props;

  const formContext = useApprovalChainFormContext(props.initialValues, props.stepRoles);
  const resolver = useMemo(
    () => zodResolver(approvalChainFormSchemaFor(formContext)),
    [formContext],
  );
  const form = useForm<ApprovalChainFormInput, undefined, ApprovalChainFormData>({
    resolver,
    defaultValues: props.initialValues ?? createDefaultFormData(),
    mode: 'onTouched',
  });

  const { control, handleSubmit } = form;
  useShowSavedStepShortfalls(form, formContext);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'steps',
    // Use our client-side tempId as the field key so dnd ids stay stable
    // across reorders (rhf's default `id` also works, but tempId is the same
    // value we thread into the DTO-less step model + tests).
    keyName: 'fieldId',
  });

  const sortableIds = useMemo(() => fields.map((field) => field.tempId), [fields]);
  const canAddStep = fields.length < MAX_STEPS;
  const canRemoveStep = fields.length > 1;

  const { sensors, handleDragEnd } = useSortableList(sortableIds, ({ oldIndex, newIndex }) =>
    move(oldIndex, newIndex),
  );

  const onValid = (data: ApprovalChainFormData) => {
    props.onSubmit(mapFormDataToDto(data));
  };

  const { setError } = form;
  const { autoApplyConflictMessage } = props;
  // Focus brings the message into view when the fields are scrolled past it, and has screen
  // readers announce the refused save through the checkbox's invalid state and description.
  useEffect(() => {
    if (!autoApplyConflictMessage) return;
    setError(
      'autoApplyToNewFolders',
      { type: 'server', message: autoApplyConflictMessage },
      { shouldFocus: true },
    );
  }, [autoApplyConflictMessage, setError]);

  const { isDirty } = form.formState;
  const { onDirtyChange } = props;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Form {...form}>
      <form
        onSubmit={(event) => void handleSubmit(onValid)(event)}
        className="flex min-h-0 flex-1 flex-col gap-md"
        data-testid="approval-chain-form"
      >
        {/* The actions stay in view while the fields scroll, so a long chain never hides Save. */}
        <div className="flex min-h-0 flex-1 flex-col gap-xl overflow-auto pr-1">
          <Card compact variant="outline" className="shadow-none">
            <CardContent className="flex flex-col gap-4">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('manageApprovalChains.approvalChainForm.approvalChainName')}
                      <span aria-hidden="true" className="text-destructive">
                        {' '}
                        *
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        size="lg"
                        disabled={isSubmitting}
                        placeholder={t(
                          'manageApprovalChains.approvalChainForm.eGStandardContentApproval',
                        )}
                        data-testid="approval-chain-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="autoApplyToNewFolders"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        ref={field.ref}
                        checked={field.value}
                        disabled={isSubmitting}
                        onCheckedChange={(next) => field.onChange(next === true)}
                        data-testid="approval-chain-auto-apply-new"
                      />
                    </FormControl>
                    <div className="flex flex-col gap-0.5">
                      <FormLabel className="font-normal">
                        {t('manageApprovalChains.approvalChainForm.autoApplyToNewFolders')}
                      </FormLabel>
                      <FormDescription className="text-foreground-subtle text-xs">
                        {t('manageApprovalChains.approvalChainForm.newFoldersCreatedInThis')}
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="autoApplyToExistingFolders"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        disabled={isSubmitting}
                        onCheckedChange={(next) => field.onChange(next === true)}
                        data-testid="approval-chain-auto-apply-existing"
                      />
                    </FormControl>
                    <div className="flex flex-col gap-0.5">
                      <FormLabel className="font-normal">
                        {t('manageApprovalChains.approvalChainForm.applyToExistingFolders')}
                      </FormLabel>
                      <FormDescription className="text-foreground-subtle text-xs">
                        {t('manageApprovalChains.approvalChainForm.oneTimeActionOnSave')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="executionMode"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value === 'in_order'}
                        disabled={isSubmitting}
                        onCheckedChange={(next) =>
                          field.onChange(next === true ? 'in_order' : 'any_order')
                        }
                        data-testid="approval-chain-run-in-order"
                      />
                    </FormControl>
                    <div className="flex flex-col gap-0.5">
                      <FormLabel className="font-normal">
                        {t('manageApprovalChains.approvalChainForm.runStepsInOrder')}
                      </FormLabel>
                      <FormDescription className="text-foreground-subtle text-xs">
                        {t('manageApprovalChains.approvalChainForm.eachStepUnlocksOnlyAfter')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                {t('manageApprovalChains.approvalChainForm.approvalSteps')}
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append(createEmptyStep())}
                disabled={isSubmitting || !canAddStep}
                data-testid="approval-chain-add-step"
              >
                <PlusIcon aria-hidden className="size-4" />
                {t('manageApprovalChains.approvalChainForm.addStep')}
              </Button>
            </div>

            {/* Array-level errors (min 1 / max 6 steps) render on the root. */}
            {form.formState.errors.steps?.root?.message && (
              <p className="text-sm text-destructive" data-testid="approval-chain-steps-error">
                {form.formState.errors.steps.root.message}
              </p>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-3">
                  {fields.map((field, index) => (
                    <ApprovalChainStepRow
                      key={field.fieldId}
                      tempId={field.tempId}
                      index={index}
                      members={members}
                      isLoadingMembers={isLoadingMembers}
                      workspaceId={workspaceId}
                      stepRoles={props.stepRoles}
                      canRemove={canRemoveStep}
                      canMoveUp={index > 0}
                      canMoveDown={index < fields.length - 1}
                      lastIndex={fields.length - 1}
                      disabled={isSubmitting}
                      onRemove={remove}
                      onMove={move}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 pt-5">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={props.onCancel}
            disabled={isSubmitting}
            data-testid="approval-chain-cancel"
          >
            {t('manageApprovalChains.approvalChainForm.cancel')}
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            data-testid="approval-chain-submit"
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
